import { Button, Textarea } from "@dd/ui";
import { useMutation } from "@tanstack/react-query";
import { Bot, Send, X } from "lucide-react";
import { useState } from "react";
import { api } from "../lib/api.ts";

const EXAMPLES = [
  "¿Cuántos pacientes tenemos?",
  "¿Cuántos turnos hay hoy?",
  "¿Cuál es el margen de hoy?",
  "¿Cuántos documentos están pendientes?",
] as const;

export function PlatformAssistant() {
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const query = useMutation({
    mutationFn: (value: string) => api.assistant.query(value),
  });

  const ask = (value = question) => {
    const trimmed = value.trim();
    if (trimmed.length < 3) return;
    setQuestion(trimmed);
    query.mutate(trimmed);
  };

  if (!open) {
    return (
      /*
       * Un círculo en el teléfono, la pastilla con su nombre en escritorio.
       *
       * Con el nombre escrito mide 186 px de ancho sobre una pantalla de 390 y
       * flota fija abajo a la derecha, que es justo donde caen las acciones de
       * cada pantalla: tapaba «Archivar ficha» en la ficha del paciente,
       * «Guardar cambios» en el perfil de la organización y los «Reponer» del
       * tablero. Medido con `elementFromPoint`, no a ojo: el navegador
       * devolvía el asistente cuando se le preguntaba qué había sobre esos
       * botones.
       *
       * Achicarlo a 48 px no elimina la superposición —ningún botón flotante
       * puede— pero la reduce a una esquina, y el relleno inferior de la
       * pantalla ya deja lugar para que el final de la página pase por debajo.
       * El nombre accesible no cambia, así que quien usa lector de pantalla
       * escucha lo mismo en los dos tamaños.
       */
      <Button
        data-tour="assistant"
        type="button"
        className="fixed bottom-5 right-5 z-40 h-12 w-12 rounded-full p-0 shadow-lg sm:h-auto sm:w-auto sm:px-4 sm:py-2 print:hidden"
        aria-label="Abrir asistente de Ronda"
        onClick={() => setOpen(true)}
      >
        <Bot className="h-5 w-5 sm:h-4 sm:w-4" />
        <span className="hidden sm:inline">Preguntale a Ronda</span>
      </Button>
    );
  }

  return (
    <aside
      className="fixed bottom-5 right-5 z-40 w-[min(24rem,calc(100vw-2rem))] rounded-xl border border-border bg-card p-4 shadow-xl print:hidden"
      aria-label="Asistente de Ronda"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-semibold">
            <Bot className="h-4 w-4 text-primary" /> Asistente de Ronda
          </h2>
          <p className="text-xs text-muted-foreground">
            Responde con datos de tu organización y respeta tus permisos.
          </p>
        </div>
        <button
          type="button"
          className="rounded p-1 text-muted-foreground hover:bg-muted"
          aria-label="Cerrar asistente"
          onClick={() => setOpen(false)}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {EXAMPLES.map((example) => (
          <button
            type="button"
            key={example}
            className="rounded-full border border-border px-2 py-1 text-left text-[11px] text-muted-foreground hover:border-primary hover:text-foreground"
            onClick={() => ask(example)}
          >
            {example}
          </button>
        ))}
      </div>

      {(query.data || query.isPending || query.isError) && (
        <div
          className="mt-3 rounded-md bg-muted/60 p-3 text-sm"
          role="status"
          aria-live="polite"
        >
          {query.isPending
            ? "Consultando datos…"
            : query.isError
              ? query.error.message
              : query.data?.answer}
          {query.data && query.data.sources.length > 0 && (
            <p className="mt-2 text-[11px] text-muted-foreground">
              Fuentes: {query.data.sources.join(", ")}, actualizado ahora
            </p>
          )}
        </div>
      )}

      <form
        className="mt-3 flex items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          ask();
        }}
      >
        <Textarea
          rows={2}
          value={question}
          maxLength={500}
          aria-label="Pregunta para el asistente"
          placeholder="Escribí una pregunta operativa…"
          onInput={(event) => setQuestion(event.currentTarget.value)}
        />
        <Button
          type="submit"
          size="sm"
          aria-label="Enviar pregunta"
          disabled={query.isPending || question.trim().length < 3}
        >
          <Send className="h-4 w-4" />
        </Button>
      </form>
      <p className="mt-2 text-[10px] text-muted-foreground">
        Verificá decisiones importantes. El asistente no modifica datos.
      </p>
    </aside>
  );
}
